import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  offset,
  type Placement,
  type Strategy,
  useClick,
  useFloating,
  useInteractions
} from '@floating-ui/react';
import { cloneElement, ComponentProps, JSX, useMemo, useRef } from 'react';
import { SPACE_4 } from '../../tokens';

type Args = {
  anchor: JSX.Element;
  arrowColor?: ComponentProps<typeof FloatingArrow>['fill'];
  placement: Placement;
  strategy?: Strategy;
};

const usePopover = ({ anchor, arrowColor, placement, strategy }: Args) => {
  const arrowRef = useRef<SVGSVGElement>(null);

  const { refs, floatingStyles, context } = useFloating({
    placement,
    strategy,
    middleware: [flip(), offset({ mainAxis: SPACE_4, crossAxis: 0 }), arrowColor ? arrow({ element: arrowRef }) : undefined],
    whileElementsMounted: autoUpdate
  });

  const anchorElement = useMemo(() => cloneElement(anchor, { ref: refs.setReference }), [anchor, refs]);
  const popoverRef = refs.setFloating;
  const floatingRef = refs.floating;

  const click = useClick(context);
  const { getFloatingProps } = useInteractions([click]);

  const arrowElement = useMemo(
    () => (arrowColor ? <FloatingArrow ref={arrowRef} context={context} fill={arrowColor} /> : null),
    [arrowRef, context, arrowColor]
  );

  return useMemo(
    () => ({
      anchorElement,
      arrowElement,
      floatingRef,
      popoverInteractiveProps: getFloatingProps(),
      popoverRef,
      popoverStyle: floatingStyles
    }),
    [anchorElement, arrowElement, floatingRef, getFloatingProps, floatingStyles, popoverRef]
  );
};

export default usePopover;
